import React, { useCallback } from 'react';
import { useProductFilters } from '@client/feature/product/hooks/useProductFilters';
import { useProducts } from '@client/feature/product/hooks/useProduct';
import { FilterPanels, BackgroundSection } from './partials';
import Result from '@client/feature/product/components/Search/Result/Result';
import { useTranslation } from 'react-i18next';
import { Spin } from 'antd';
import { usePageMeta } from '@client/hooks/usePageMeta';

/**
 * Main search page component for product with filters and results
 * Supports cold start from shared URLs with ID-based filters
 * @returns {JSX.Element} The product search page
 */
const ProductSearchPage = () => {
  const {
    filters,
    filterLabels,
    updateFilterWithLabels,
    updatePagination,
    updateSorting,
    getCurrentSorting,
    resetFilters,
    hasActiveFilters,
    getFilterLabel,
    isLoadingLabels,
    enrichedData,
    getLabelsForField,
  } = useProductFilters();

  const { data, isLoading, error } = useProducts(filters);
  const { t } = useTranslation();
  usePageMeta({
    title: t('seo|product.searchPageTitle'),
    description: t('seo|product.searchPageDescription'),
    breadcrumbTitle: t('breadcrumb|product.searchTitle'),
    breadcrumbDescription: t('breadcrumb|product.searchDescription'),
  });
  const handlePageChange = useCallback(
    ({ page, size }) => {
      updatePagination({ page, size });
    },
    [updatePagination]
  );
  const handleSorting = useCallback(
    (field, order) => updateSorting(field, order),
    [updateSorting]
  );
  const handleReset = useCallback(() => resetFilters(), [resetFilters]);
  const handleRemoveFilter = useCallback(
    (filterKey, valueId) => {
      const currentValues = filters[filterKey] || [];
      const newValues = currentValues.filter((v) => v !== valueId);
      updateFilterWithLabels(filterKey, newValues);
    },
    [filters, updateFilterWithLabels]
  );

  // Show loading spinner during cold start label resolution
  if (isLoadingLabels) {
    return (
      <div className="relative w-full min-h-screen">
        <div className="container">
          <BackgroundSection showBottomBg={false} />
          <div className="flex justify-center items-center py-20">
            <Spin size="large" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="relative w-full min-h-screen">
      <div className="container">
        <BackgroundSection showBottomBg={false} />
        <div className="flex flex-col gap-8 relative z-10">
          <div className="filters-container">
            <FilterPanels
              filters={filters}
              onFilterChange={updateFilterWithLabels}
              filterLabels={filterLabels}
              onReset={handleReset}
            />
          </div>

          <Result
            filters={filters}
            data={data}
            isLoading={isLoading}
            error={error}
            getFilterLabel={getFilterLabel}
            onResetFilters={handleReset}
            onRemoveFilter={handleRemoveFilter}
            onPageChange={handlePageChange}
            onSortChange={handleSorting}
            currentSorting={getCurrentSorting()}
          />
        </div>
        {!isLoading && (
          <>
            <p>{t('product|table.bottomText1')}</p>
            <p className="mb-4">{t('product|table.bottomText2')}</p>
          </>
        )}
      </div>
    </div>
  );
};

export default ProductSearchPage;
